/**
 * DOM → SVG vectoriser.
 *
 * The usual html-to-image style export wraps the whole DOM in a single
 * <foreignObject>, which Figma imports as one flat picture. This walks the tree
 * instead and emits primitives Figma understands natively:
 *
 *   background  → <rect>   → Figma rectangle
 *   border      → <rect> / <line> → Figma rectangle / line
 *   text        → <text>   → Figma text layer, one per rendered line
 *   inline svg  → copied through → Figma vector layers
 *   <img>       → <image>  → Figma image fill, embedded as a data URI
 *
 * The result is fully editable after pasting: real text you can retype, real
 * shapes you can restyle. Effects CSS can express but SVG cannot map cleanly
 * (box-shadow, backdrop blur, gradients) are intentionally dropped rather than
 * faked.
 */

const TRANSPARENT = /^(transparent|rgba\(0,\s*0,\s*0,\s*0\))$/;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const num = (v: string) => parseFloat(v) || 0;
const round = (n: number) => Math.round(n * 100) / 100;
type Ctx = {
  ox: number;
  oy: number;
  out: string[];
  defs: string[];
  /** Remote image URL → embedded data URI. */
  images: Map<string, string>;
  ids: number;
};

/** Emitted only when the paint is actually visible. */
function paintable(color: string) {
  return !!color && !TRANSPARENT.test(color.replace(/\s/g, ''));
}

/**
 * Brand logos are the one thing a pasted SVG cannot fetch for itself: Figma
 * does not resolve remote hrefs, so linked marks arrive as empty boxes. Every
 * image in the subtree is fetched up front and embedded as a data URI instead.
 */
const imageCache = new Map<string, string | null>();

/** Blob → data URI. */
function readAsDataUri(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Direct fetch. Works for hosts that send permissive CORS headers, and is the
 * only strategy that preserves an SVG mark as an SVG.
 */
async function viaFetch(url: string) {
  const res = await fetch(url, {
    mode: 'cors',
    cache: 'force-cache'
  });
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  const data = await readAsDataUri(blob);
  // An SVG nested inside <image> is not reliably rendered by every consumer, so
  // vector marks are rasterised once here. The data URI is same-origin, so the
  // canvas stays untainted.
  if (/svg/i.test(blob.type)) return await viaCanvas(data);
  return data;
}

/**
 * Re-load through an <img> with crossOrigin set and rasterise via canvas. This
 * catches hosts that allow cross-origin *images* but not fetch, which is the
 * common case for CDN-hosted brand assets.
 */
function viaCanvas(url: string) {
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'sync';
    img.onload = () => {
      try {
        // Rasterised at 3× so a 16px mark still holds up when the frame is
        // scaled up in Figma.
        const scale = 3;
        const w = Math.max(1, Math.round((img.naturalWidth || 64) * scale));
        const h = Math.max(1, Math.round((img.naturalHeight || 64) * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const g = canvas.getContext('2d');
        if (!g) throw new Error('no 2d context');
        g.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('load failed'));
    img.src = url;
  });
}

/**
 * Last resort: an image proxy that always answers with permissive CORS headers.
 * Needed for the assets served from buckets with no CORS configuration at all,
 * which no amount of client-side coaxing can read.
 */
function viaProxy(url: string) {
  return viaFetch(`https://wsrv.nl/?url=${encodeURIComponent(url)}&output=png&w=256`);
}

/** A second proxy, because the first one rate-limits a burst of requests. */
function viaBackupProxy(url: string) {
  return viaFetch(`https://corsproxy.io/?url=${encodeURIComponent(url)}`);
}
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Brand logos are the one thing a pasted SVG cannot fetch for itself, so each
 * one has to be embedded. Three strategies are tried in order because the marks
 * come from three kinds of host — a CORS-friendly icon CDN, a logo service, and
 * a plain asset bucket — and only the first of those answers a fetch.
 */
async function embed(url: string): Promise<string | null> {
  const cached = imageCache.get(url);
  if (cached) return cached;
  let data: string | null = null;
  for (const strategy of [viaFetch, viaCanvas, viaProxy, viaBackupProxy]) {
    // Two attempts per strategy: the proxies rate-limit a burst of a dozen
    // logos, and a rejected request succeeds a moment later. Without the retry
    // a handful of marks came out empty at random — which is exactly what
    // "some show, some don't" looked like.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        data = await strategy(url);
        if (data) break;
      } catch {
        data = null;
        if (attempt === 0) await wait(220);
      }
    }
    if (data) break;
  }
  // Successes are cached; failures are NOT, so a transient rate-limit does not
  // permanently blank that logo for the rest of the session.
  if (data) imageCache.set(url, data);
  return data;
}
/**
 * Warm the cache for a subtree ahead of time.
 *
 * This exists for the clipboard, not for speed: `navigator.clipboard.writeText`
 * requires the document to be focused at the moment it is called, and awaiting
 * a dozen logo fetches in between the click and the write is long enough to
 * lose that. Fetching the marks before the user presses Copy keeps the write
 * effectively synchronous.
 */
export async function preloadImages(root: HTMLElement): Promise<void> {
  await collectImages(root);
}
/**
 * Bake a silhouette into the pixels.
 *
 * The site turns Giga's mark white with `filter: brightness(0) invert(1)`, and
 * the obvious SVG translation is an feColorMatrix — but Figma discards SVG
 * filters on import, so the mark arrived in its original colours sitting on an
 * orange tile. Recolouring the raster itself is the only version that survives.
 */
async function tint(dataUri: string, colour: string): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('load failed'));
    el.src = dataUri;
  });
  const w = Math.max(1, img.naturalWidth || 64);
  const h = Math.max(1, img.naturalHeight || 64);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d');
  if (!g) return dataUri;
  g.drawImage(img, 0, 0, w, h);
  // Keep the mark's alpha, replace every colour with the flat one.
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = colour;
  g.fillRect(0, 0, w, h);
  return canvas.toDataURL('image/png');
}

/** Cache key for an image, since the same mark can appear tinted and not. */
const imageKey = (src: string, mark: 'white' | 'black' | null) => mark ? `${src}||${mark}` : src;
async function collectImages(root: HTMLElement): Promise<Map<string, string>> {
  const urls = new Set<string>();
  const tints = new Map<string, 'white' | 'black'>();
  root.querySelectorAll('img').forEach(node => {
    const img = node as HTMLImageElement;
    const src = img.currentSrc || img.src;
    if (!src) return;
    const mark = silhouette(getComputedStyle(img).filter);
    if (mark) tints.set(src, mark);
    if (!src.startsWith('data:')) urls.add(src);
  });
  // Four at a time rather than all at once: firing a dozen parallel requests at
  // the same proxy is what triggered its rate limiting in the first place.
  const map = new Map<string, string>();
  const queue = [...urls];
  const workers = Array.from({
    length: Math.min(4, queue.length)
  }, async () => {
    for (let url = queue.shift(); url; url = queue.shift()) {
      const data = await embed(url);
      if (!data) continue;
      map.set(url, data);
      const mark = tints.get(url);
      if (mark) {
        try {
          map.set(imageKey(url, mark), await tint(data, mark === 'white' ? '#FFFFFF' : '#000000'));
        } catch {
          // Fall back to the untinted mark rather than dropping it entirely.
        }
      }
    }
  });
  await Promise.all(workers);
  return map;
}

/**
 * Detect the CSS the site uses to flatten Giga's mark to one colour, so the
 * same effect can be baked into the exported pixels.
 */
function silhouette(filter: string): 'white' | 'black' | null {
  if (!filter || filter === 'none') return null;
  const inverted = /invert\((1|100%)\)/.test(filter);
  const flattened = /brightness\(0\)/.test(filter);
  if (flattened && inverted) return 'white';
  if (flattened) return 'black';
  return null;
}

/** Split a CSS function's argument list on top-level commas only. */
function splitArgs(value: string) {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of value) {
    if (ch === '(') depth++;
    if (ch === ')') depth--;
    if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
    } else current += ch;
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}

/**
 * Linear gradients, emitted as real SVG gradients.
 *
 * These were being dropped as "something SVG cannot express", which was wrong —
 * SVG and Figma both handle linear gradients natively. The cost of dropping
 * them was visible: the report card inside the Slack thread is a peach gradient
 * with white bars on top, so it exported as an empty white panel.
 */
function gradientDef(backgroundImage: string, id: string): string | null {
  const match = /^linear-gradient\((.*)\)$/.exec(backgroundImage.trim());
  if (!match) return null;
  const args = splitArgs(match[1]);
  if (args.length < 2) return null;
  let coords = {
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 1
  };
  let stopArgs = args;
  const first = args[0];
  if (/^(to\s|\d|-|calc)/.test(first) && !/^(rgb|hsl|#|[a-z]+$)/i.test(first)) {
    stopArgs = args.slice(1);
    const deg = /(-?[\d.]+)deg/.exec(first);
    if (deg) {
      // CSS angles run clockwise from "to top"; SVG works in unit coordinates.
      const rad = (parseFloat(deg[1]) - 90) * Math.PI / 180;
      const dx = Math.cos(rad);
      const dy = Math.sin(rad);
      coords = {
        x1: 0.5 - dx / 2,
        y1: 0.5 - dy / 2,
        x2: 0.5 + dx / 2,
        y2: 0.5 + dy / 2
      };
    } else {
      const right = /right/.test(first);
      const left = /left/.test(first);
      const bottom = /bottom/.test(first);
      const top = /top/.test(first);
      coords = {
        x1: right ? 0 : left ? 1 : 0,
        y1: bottom ? 0 : top ? 1 : 0,
        x2: right ? 1 : left ? 0 : 0,
        y2: bottom ? 1 : top ? 0 : 0
      };
      if (!right && !left && !bottom && !top) coords = {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 1
      };
    }
  }
  const stops = stopArgs.map((raw, i) => {
    const position = /\s([\d.]+)%\s*$/.exec(raw);
    const colour = position ? raw.slice(0, position.index).trim() : raw;
    const offset = position ? parseFloat(position[1]) : stopArgs.length === 1 ? 0 : i / (stopArgs.length - 1) * 100;
    return `<stop offset="${round(offset)}%" stop-color="${colour}"/>`;
  });
  if (!stops.length) return null;
  return `<linearGradient id="${id}" x1="${coords.x1}" y1="${coords.y1}" x2="${coords.x2}" y2="${coords.y2}">${stops.join('')}</linearGradient>`;
}

/** Dashed and dotted rules are structural on these screens, not decoration. */
function dashFor(style: string, width: number) {
  if (style === 'dashed') return ` stroke-dasharray="${round(width * 3)} ${round(width * 2)}"`;
  if (style === 'dotted') return ` stroke-dasharray="${round(width)} ${round(width * 1.6)}" stroke-linecap="round"`;
  return '';
}

/** A rounded box, honouring each corner independently. */
function boxPath(x: number, y: number, w: number, h: number, radii: number[]) {
  const max = Math.min(w, h) / 2;
  const [tl, tr, br, bl] = radii.map(v => Math.min(v, max));
  return [`M${round(x + tl)} ${round(y)}`, `H${round(x + w - tr)}`, tr ? `A${round(tr)} ${round(tr)} 0 0 1 ${round(x + w)} ${round(y + tr)}` : '', `V${round(y + h - br)}`, br ? `A${round(br)} ${round(br)} 0 0 1 ${round(x + w - br)} ${round(y + h)}` : '', `H${round(x + bl)}`, bl ? `A${round(bl)} ${round(bl)} 0 0 1 ${round(x)} ${round(y + h - bl)}` : '', `V${round(y + tl)}`, tl ? `A${round(tl)} ${round(tl)} 0 0 1 ${round(x + tl)} ${round(y)}` : '', 'Z'].filter(Boolean).join(' ');
}
function emitBox(cs: CSSStyleDeclaration, r: DOMRect, ctx: Ctx) {
  const x = round(r.left - ctx.ox);
  const y = round(r.top - ctx.oy);
  const w = round(r.width);
  const h = round(r.height);
  if (w <= 0 || h <= 0) return;
  const radii = [num(cs.borderTopLeftRadius), num(cs.borderTopRightRadius), num(cs.borderBottomRightRadius), num(cs.borderBottomLeftRadius)];
  const uniformRadius = radii.every(v => v === radii[0]);
  const rx = radii[0] ? ` rx="${round(Math.min(radii[0], Math.min(w, h) / 2))}"` : '';
  const paintFill = (fill: string) => {
    ctx.out.push(uniformRadius ? `<rect x="${x}" y="${y}" width="${w}" height="${h}"${rx} fill="${fill}"/>` : `<path d="${boxPath(x, y, w, h, radii)}" fill="${fill}"/>`);
  };
  if (paintable(cs.backgroundColor)) paintFill(cs.backgroundColor);
  const gradient = gradientDef(cs.backgroundImage, `grad-${ctx.ids}`);
  if (gradient) {
    ctx.defs.push(gradient);
    paintFill(`url(#grad-${ctx.ids++})`);
  }

  // Borders. A uniform border becomes one stroked shape; asymmetric borders —
  // the hairline dividers used all over these screens — become single lines,
  // which is what they actually are.
  const sides = [['Top', cs.borderTopWidth, cs.borderTopColor, cs.borderTopStyle], ['Right', cs.borderRightWidth, cs.borderRightColor, cs.borderRightStyle], ['Bottom', cs.borderBottomWidth, cs.borderBottomColor, cs.borderBottomStyle], ['Left', cs.borderLeftWidth, cs.borderLeftColor, cs.borderLeftStyle]] as const;
  const widths = sides.map(s => num(s[1]));
  const uniform = widths.every(v => v === widths[0]) && widths[0] > 0 && sides.every(s => s[2] === cs.borderTopColor);
  if (uniform && paintable(cs.borderTopColor)) {
    const bw = widths[0];
    const o = bw / 2;
    const dash = dashFor(cs.borderTopStyle, bw);
    ctx.out.push(uniformRadius ? `<rect x="${round(x + o)}" y="${round(y + o)}" width="${round(w - bw)}" height="${round(h - bw)}"${rx} fill="none" stroke="${cs.borderTopColor}" stroke-width="${bw}"${dash}/>` : `<path d="${boxPath(x + o, y + o, w - bw, h - bw, radii)}" fill="none" stroke="${cs.borderTopColor}" stroke-width="${bw}"${dash}/>`);
  } else {
    sides.forEach(([side, , color, style], i) => {
      const bw = widths[i];
      if (!bw || !paintable(color)) return;
      const o = bw / 2;
      const line = side === 'Top' ? [x, y + o, x + w, y + o] : side === 'Bottom' ? [x, y + h - o, x + w, y + h - o] : side === 'Left' ? [x + o, y, x + o, y + h] : [x + w - o, y, x + w - o, y + h];
      ctx.out.push(`<line x1="${round(line[0])}" y1="${round(line[1])}" x2="${round(line[2])}" y2="${round(line[3])}" stroke="${color}" stroke-width="${bw}"${dashFor(style, bw)}/>`);
    });
  }
}

/**
 * Text, one <text> per rendered line. Measuring word by word and grouping by
 * line box is what keeps wrapped paragraphs as separate editable lines instead
 * of one run that Figma re-wraps differently.
 */
function emitText(node: Text, cs: CSSStyleDeclaration, ctx: Ctx) {
  const raw = node.textContent ?? '';
  if (!raw.trim()) return;
  const size = num(cs.fontSize);
  const lines = new Map<number, {
    left: number;
    right: number;
    top: number;
    height: number;
    words: string[];
  }>();
  const range = document.createRange();
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw))) {
    range.setStart(node, m.index);
    range.setEnd(node, m.index + m[0].length);
    const rects = range.getClientRects();
    if (!rects.length) continue;
    const b = rects[0];
    const key = Math.round(b.top);
    const line = lines.get(key);
    if (line) {
      line.left = Math.min(line.left, b.left);
      line.right = Math.max(line.right, b.right);
      line.words.push(m[0]);
    } else {
      lines.set(key, {
        left: b.left,
        right: b.right,
        top: b.top,
        height: b.height,
        words: [m[0]]
      });
    }
  }
  range.detach?.();
  const align = cs.textAlign;
  const spacing = cs.letterSpacing === 'normal' ? '' : ` letter-spacing="${num(cs.letterSpacing)}"`;
  const style = cs.fontStyle === 'italic' ? ' font-style="italic"' : '';
  const decoration = cs.textDecorationLine && cs.textDecorationLine !== 'none' ? ` text-decoration="${cs.textDecorationLine}"` : '';
  // Quote each family so Figma matches the name rather than the whole stack.
  const family = cs.fontFamily.split(',')[0].replace(/["']/g, '');
  const transform = cs.textTransform;
  for (const line of lines.values()) {
    // Alphabetic baseline sits roughly 80% of the em down from the cap line;
    // centring that within the line box lands text where the browser drew it.
    const baseline = line.top - ctx.oy + (line.height - size) / 2 + size * 0.8;
    const anchor = align === 'center' ? 'middle' : align === 'right' || align === 'end' ? 'end' : 'start';
    const x = anchor === 'middle' ? (line.left + line.right) / 2 : anchor === 'end' ? line.right : line.left;
    // text-transform is a CSS-only concept, so the transformed string is what
    // gets written out — otherwise every small-caps label exports lower case.
    let content = line.words.join(' ');
    if (transform === 'uppercase') content = content.toUpperCase();else if (transform === 'lowercase') content = content.toLowerCase();
    ctx.out.push(`<text x="${round(x - ctx.ox)}" y="${round(baseline)}" font-family="${esc(family)}" font-size="${round(size)}" font-weight="${cs.fontWeight}" fill="${cs.color}"${spacing}${style}${decoration} text-anchor="${anchor}" xml:space="preserve">${esc(content)}</text>`);
  }
}
function walk(el: Element, ctx: Ctx) {
  const cs = getComputedStyle(el);
  const opacity = num(cs.opacity);
  if (cs.display === 'none' || cs.visibility === 'hidden' || opacity === 0) return;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return;

  // Partial opacity used to be ignored, so dimmed rows exported at full
  // strength. Wrapping the subtree keeps the browser's rendering.
  const faded = opacity > 0 && opacity < 1;
  if (faded) ctx.out.push(`<g opacity="${round(opacity)}">`);

  // Inline SVG (every icon on these screens) is already vector — copy it in
  // place rather than trying to re-describe it.
  if (el instanceof SVGSVGElement) {
    const clone = el.cloneNode(true) as SVGSVGElement;
    clone.removeAttribute('class');
    if (!clone.getAttribute('width')) clone.setAttribute('width', String(round(r.width)));
    if (!clone.getAttribute('height')) clone.setAttribute('height', String(round(r.height)));
    // Lucide strokes inherit currentColor, which means nothing once detached —
    // resolve it on the root and on any child that names it explicitly.
    if (clone.getAttribute('stroke') === 'currentColor') clone.setAttribute('stroke', cs.color);
    if (clone.getAttribute('fill') === 'currentColor') clone.setAttribute('fill', cs.color);
    clone.querySelectorAll('*').forEach(child => {
      if (child.getAttribute('stroke') === 'currentColor') child.setAttribute('stroke', cs.color);
      if (child.getAttribute('fill') === 'currentColor') child.setAttribute('fill', cs.color);
    });
    ctx.out.push(`<g transform="translate(${round(r.left - ctx.ox)},${round(r.top - ctx.oy)})">${clone.outerHTML}</g>`);
    if (faded) ctx.out.push('</g>');
    return;
  }
  emitBox(cs, r, ctx);
  if (el instanceof HTMLImageElement) {
    const src = el.currentSrc || el.src;
    // A silhouetted mark has already been recoloured in the pre-pass, so the
    // pixels are correct and no SVG filter is needed — which matters because
    // Figma drops SVG filters on import.
    const mark = silhouette(cs.filter);
    // If every embed strategy failed, the remote URL still goes out: it renders
    // when the file is opened in a browser, which is more useful than a gap.
    const href = src?.startsWith('data:') ? src : ctx.images.get(imageKey(src, mark)) ?? ctx.images.get(src) ?? src;
    if (href) {
      ctx.out.push(`<image href="${esc(href)}" x="${round(r.left - ctx.ox)}" y="${round(r.top - ctx.oy)}" width="${round(r.width)}" height="${round(r.height)}" preserveAspectRatio="xMidYMid meet"/>`);
    }
    if (faded) ctx.out.push('</g>');
    return;
  }

  // Anything the browser clips, the export clips too. Without this, a
  // scrollable list exports every row it is hiding, painted straight over the
  // frame it is meant to sit inside.
  const clips = /hidden|auto|scroll|clip/.test(cs.overflow) || /hidden|auto|scroll|clip/.test(cs.overflowY);
  let clipped = false;
  if (clips) {
    const id = `clip-${ctx.ids++}`;
    const radii = [num(cs.borderTopLeftRadius), num(cs.borderTopRightRadius), num(cs.borderBottomRightRadius), num(cs.borderBottomLeftRadius)];
    ctx.defs.push(`<clipPath id="${id}"><path d="${boxPath(round(r.left - ctx.ox), round(r.top - ctx.oy), round(r.width), round(r.height), radii)}"/></clipPath>`);
    ctx.out.push(`<g clip-path="url(#${id})">`);
    clipped = true;
  }
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      emitText(child as Text, cs, ctx);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      walk(child as Element, ctx);
    }
  }
  if (clipped) ctx.out.push('</g>');
  if (faded) ctx.out.push('</g>');
}

/**
 * Serialise an on-screen element to editable SVG markup. Async because every
 * logo in the subtree is fetched and embedded first — a pasted SVG cannot load
 * remote images, so linked marks would otherwise arrive empty.
 */
export async function domToSvg(root: HTMLElement, background = '#FFFFFF'): Promise<string> {
  const images = await collectImages(root);
  const r = root.getBoundingClientRect();
  const ctx: Ctx = {
    ox: r.left,
    oy: r.top,
    out: [],
    defs: [],
    images,
    ids: 0
  };
  walk(root, ctx);
  const w = round(r.width);
  const h = round(r.height);
  return [`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`, ctx.defs.length ? `<defs>${ctx.defs.join('')}</defs>` : '', `<rect width="${w}" height="${h}" fill="${background}"/>`, ...ctx.out, '</svg>'].join('');
}
